/**
 * StoryEngine.ts — Heart of the game.
 *
 * Loads story nodes, filters available choices based on game state,
 * applies effects, and advances the player through the narrative.
 */

import type {
  StoryNode,
  StoryChoice,
  NodeEffect,
  ChoiceRequirement,
  NodeIndex,
  NodeIndexEntry,
  StatName,
} from '@game/data/types';
import type { GameState } from '@core/engine/GameState';
import type { EventSystem } from '@core/engine/EventSystem';

// ─── StoryEngine ──────────────────────────────────────────────

export class StoryEngine {
  private state: GameState;
  private events: EventSystem;
  private nodeIndex: NodeIndex | null = null;

  /** Function to build the node index (injected to avoid import.meta.glob dependency in tests). */
  private buildIndex: () => Promise<NodeIndex>;

  constructor(
    state: GameState | null,
    events: EventSystem,
    buildIndex: () => Promise<NodeIndex>,
  ) {
    this.state = state!;
    this.events = events;
    this.buildIndex = buildIndex;
  }

  /** Update the game state reference (called after character creation or save load). */
  setState(state: GameState): void {
    this.state = state;
  }

  // ─── Initialization ───────────────────────────────────────

  /** Build the node index. Call during loading screen. */
  async initialize(): Promise<void> {
    this.nodeIndex = await this.buildIndex();
  }

  /** Set a pre-built node index (useful for testing). */
  setNodeIndex(index: NodeIndex): void {
    this.nodeIndex = index;
  }

  // ─── Node Access ──────────────────────────────────────────

  /** Get the current story node. */
  getCurrentNode(): StoryNode | undefined {
    return this.getNodeById(this.state.story.currentNodeId);
  }

  /** Get a node by ID from the index. */
  getNodeById(nodeId: string): StoryNode | undefined {
    return this.nodeIndex?.get(nodeId)?.node;
  }

  /** Get the full index entry (includes chapter number). */
  getNodeEntry(nodeId: string): NodeIndexEntry | undefined {
    return this.nodeIndex?.get(nodeId);
  }

  // ─── Choice Filtering ────────────────────────────────────

  /** Get only the choices the player currently qualifies for. */
  getFilteredChoices(node?: StoryNode): StoryChoice[] {
    const target = node ?? this.getCurrentNode();
    if (!target) return [];
    return target.choices.filter((choice) => this.meetsRequirements(choice));
  }

  /** Check if a player meets all requirements for a choice. */
  meetsRequirements(choice: StoryChoice): boolean {
    if (!choice.requirements || choice.requirements.length === 0) return true;
    return choice.requirements.every((req) => this.evaluateRequirement(req));
  }

  /** Evaluate a single requirement against the current game state. */
  evaluateRequirement(req: ChoiceRequirement): boolean {
    switch (req.type) {
      case 'class':
        return this.state.character.classId === req.key;

      case 'trait': {
        const trait = this.state.getTrait(req.key);
        if (!trait) return false;
        const requiredLevel = typeof req.value === 'number' ? req.value : 1;
        return trait.level >= requiredLevel;
      }

      case 'flag': {
        const flagValue = this.state.getFlag(req.key);
        if (req.operator === 'equals' || req.operator === '==') {
          return flagValue === req.value;
        }
        // Default: flag must be true
        return flagValue === true;
      }

      case 'stat': {
        const statValue = this.state.getEffectiveStat(req.key as StatName);
        const threshold = typeof req.value === 'number' ? req.value : 0;
        return this.compareValue(statValue, req.operator ?? '>=', threshold);
      }

      case 'item': {
        if (req.operator === 'contains') {
          // req.key is "inventory", req.value is the item ID
          return this.state.hasItem(req.value as string);
        }
        return this.state.hasItem(req.key);
      }

      case 'relationship': {
        const relValue = this.state.getRelationship(req.key);
        const threshold = typeof req.value === 'number' ? req.value : 0;
        return this.compareValue(relValue, req.operator ?? '>=', threshold);
      }

      case 'skill': {
        const skillLevel = this.state.skillLevels.get(req.key) ?? 0;
        const requiredLevel = typeof req.value === 'number' ? req.value : 1;
        return skillLevel >= requiredLevel;
      }

      case 'faction': {
        const factionRep = this.state.factionReputation.get(req.key) ?? 0;
        const threshold = typeof req.value === 'number' ? req.value : 0;
        return this.compareValue(factionRep, req.operator ?? '>=', threshold);
      }

      default:
        return true;
    }
  }

  // ─── Story Advancement ────────────────────────────────────

  /** Advance to a specific node. Applies entry effects and records history. */
  advanceToNode(nodeId: string): StoryNode | undefined {
    const entry = this.nodeIndex?.get(nodeId);
    if (!entry) return undefined;

    const node = entry.node;
    const previousNodeId = this.state.story.currentNodeId;

    // Update story state
    this.state.story.currentNodeId = nodeId;
    this.state.story.visitedNodes.add(nodeId);

    // Update act/chapter from node data
    if (node.act) this.state.story.currentAct = node.act;
    if (node.chapter) {
      const prevChapter = this.state.story.currentChapter;
      this.state.story.currentChapter = node.chapter;
      if (node.chapter !== prevChapter) {
        this.events.emit('chapterChanged', {
          chapterNumber: node.chapter,
          act: node.act,
        });
      }
    }

    // Apply entry effects
    if (node.effects) {
      this.applyEffects(node.effects);
    }

    this.events.emit('nodeEntered', { nodeId, node, previousNodeId });

    return node;
  }

  /** Select a choice, apply its effects, and advance to the next node. */
  selectChoice(choiceId: string): StoryNode | undefined {
    const currentNode = this.getCurrentNode();
    if (!currentNode) return undefined;

    const choice = currentNode.choices.find((c) => c.id === choiceId);
    if (!choice) return undefined;

    // Record choice in history
    this.state.story.choiceHistory.push({
      nodeId: currentNode.id,
      choiceId,
      timestamp: Date.now(),
    });

    this.events.emit('choiceSelected', {
      choiceId,
      nodeId: currentNode.id,
    });

    // Determine next node
    const nextNodeId = choice.nextNodeId;

    // Advance to the next node (if specified)
    if (nextNodeId) {
      return this.advanceToNode(nextNodeId);
    }

    return undefined;
  }

  // ─── Class-Specific Text ──────────────────────────────────

  /** Get the class-specific text for the current player, or the default description. */
  getClassSpecificText(node: StoryNode): string {
    if (node.classSpecificText) {
      const classText = node.classSpecificText[this.state.character.classId];
      if (classText) return classText;
    }
    return node.description;
  }

  // ─── Effect Application ───────────────────────────────────

  /** Apply a list of effects to the game state. */
  applyEffects(effects: NodeEffect[]): void {
    for (const effect of effects) {
      this.applyEffect(effect);
    }
  }

  /** Apply a single effect to the game state. */
  applyEffect(effect: NodeEffect): void {
    switch (effect.type) {
      case 'stat':
        this.applyStatEffect(effect);
        break;
      case 'flag':
        this.applyFlagEffect(effect);
        break;
      case 'item':
        this.applyItemEffect(effect);
        break;
      case 'relationship':
        this.applyRelationshipEffect(effect);
        break;
      case 'trait':
        this.applyTraitEffect(effect);
        break;
      case 'quest':
        this.applyQuestEffect(effect);
        break;
    }

    this.events.emit('effectApplied', { effect });
  }

  // ─── Private: Effect Handlers ─────────────────────────────

  private applyStatEffect(effect: NodeEffect): void {
    const value = typeof effect.value === 'number' ? effect.value : 0;
    switch (effect.action) {
      case 'add':
      case 'increase':
        this.state.modifyStat(effect.key, value);
        break;
      case 'subtract':
      case 'decrease':
        this.state.modifyStat(effect.key, -value);
        break;
      case 'set':
        // For 'set', compute the delta from current value
        this.setStatDirect(effect.key, value);
        break;
    }
  }

  private applyFlagEffect(effect: NodeEffect): void {
    const value = typeof effect.value === 'boolean' ? effect.value : true;
    this.state.setFlag(effect.key, value);
  }

  private applyItemEffect(effect: NodeEffect): void {
    switch (effect.action) {
      case 'add':
        this.state.addItem(effect.key);
        break;
      case 'remove':
        this.state.removeItem(effect.key);
        break;
    }
  }

  private applyRelationshipEffect(effect: NodeEffect): void {
    const value = typeof effect.value === 'number' ? effect.value : 0;
    switch (effect.action) {
      case 'increase':
      case 'add':
        this.state.modifyRelationship(effect.key, value);
        break;
      case 'decrease':
      case 'subtract':
        this.state.modifyRelationship(effect.key, -value);
        break;
    }
  }

  private applyTraitEffect(effect: NodeEffect): void {
    const traitData = effect.traitData
      ? { name: effect.traitData.name, description: effect.traitData.description, category: effect.traitData.category }
      : undefined;

    switch (effect.action) {
      case 'increase':
      case 'add':
        this.state.modifyTrait(effect.key, 1, traitData);
        break;
      case 'decrease':
      case 'subtract':
      case 'remove':
        this.state.modifyTrait(effect.key, -1);
        break;
    }
  }

  private applyQuestEffect(effect: NodeEffect): void {
    const status = typeof effect.value === 'string' ? effect.value : 'active';
    this.state.setQuest(effect.key, status);
  }

  // ─── Private: Helpers ─────────────────────────────────────

  private compareValue(actual: number, operator: string, expected: number): boolean {
    switch (operator) {
      case '>=': return actual >= expected;
      case '<=': return actual <= expected;
      case '==': return actual === expected;
      case '!=': return actual !== expected;
      case '>': return actual > expected;
      case '<': return actual < expected;
      case 'equals': return actual === expected;
      default: return actual >= expected;
    }
  }

  private setStatDirect(key: string, value: number): void {
    // Special keys
    switch (key) {
      case 'experience_points':
        this.state.character.xp = value;
        break;
      case 'gold':
        this.state.gold = value;
        break;
      case 'hit_points':
        this.state.character.hp = Math.min(value, this.state.character.hpMax);
        break;
      case 'frostbite':
        this.state.character.frostbite = Math.max(0, Math.min(100, value));
        break;
      case 'corruption':
      case 'corruption_points':
        this.state.character.corruption = Math.max(0, Math.min(100, value));
        break;
      default: {
        const statNames: StatName[] = ['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'];
        if (statNames.includes(key as StatName)) {
          this.state.character.stats[key as StatName] = value;
        }
        break;
      }
    }
  }
}
